import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit3, LogOut, ArrowLeft, GripVertical, ExternalLink, Download, Mail, FileText } from 'lucide-react';
import {
  fetchAdminPosts, fetchAdminPost, createPost, updatePost, deletePost,
  clearToken, fetchMe, uploadImage, fetchLeads, updateLeadStatus,
} from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { useSEO } from '../../hooks/useSEO';

const emptyForm = {
  title: '', excerpt: '', category: '', author: 'Hei Web Creations',
  read_time: '5 min read', tags: '', cover_image: '',
  meta_title: '', meta_description: '', published_at: '', status: 'published', content: [],
};

const BLOCK_TYPES = ['paragraph', 'heading', 'quote', 'list'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState('all');
  const [tab, setTab] = useState('posts'); // posts | leads
  const [view, setView] = useState('list'); // list | editor
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useSEO({ title: 'Blog CMS | Hei Web Creations' });

  const loadPosts = useCallback(async () => {
    try {
      const data = await fetchAdminPosts();
      setPosts(data);
    } catch {
      setPosts([]);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      const data = await fetchLeads();
      setLeads(data);
    } catch {
      setLeads([]);
    }
  }, []);

  useEffect(() => {
    fetchMe().catch(() => {
      clearToken();
      navigate('/admin/login', { replace: true });
    });
    loadPosts();
    loadLeads();
  }, [loadPosts, loadLeads, navigate]);

  const exportLeadsCSV = () => {
    const headers = ['Name', 'Email', 'Service Type', 'Status', 'Project Description', 'Date'];
    const rows = leads.map((l) => [
      l.name, l.email, l.service_type, l.status || 'new',
      (l.project_description || '').replace(/"/g, '""'),
      new Date(l.created_at).toLocaleString(),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${c}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLeadStatusChange = async (leadId, status) => {
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      await updateLeadStatus(leadId, status);
    } catch {
      toast({ title: 'Error', description: 'Could not update status.', variant: 'destructive' });
      loadLeads();
    }
  };

  const LEAD_STATUSES = ['new', 'contacted', 'won', 'lost'];
  const STATUS_STYLES = {
    new: 'bg-blue-100 text-blue-700',
    contacted: 'bg-amber-100 text-amber-700',
    won: 'bg-green-100 text-green-700',
    lost: 'bg-gray-200 text-gray-600',
  };
  const filteredLeads = leadFilter === 'all'
    ? leads
    : leads.filter((l) => (l.status || 'new') === leadFilter);
  const leadCounts = LEAD_STATUSES.reduce(
    (acc, s) => ({ ...acc, [s]: leads.filter((l) => (l.status || 'new') === s).length }),
    {}
  );

  const handleLogout = () => {
    clearToken();
    navigate('/admin/login', { replace: true });
  };

  const startNew = () => {
    setForm(emptyForm);
    setEditingSlug(null);
    setView('editor');
  };

  const startEdit = async (slug) => {
    try {
      const post = await fetchAdminPost(slug);
      setForm({
        title: post.title, excerpt: post.excerpt, category: post.category,
        author: post.author, read_time: post.read_time,
        tags: (post.tags || []).join(', '), cover_image: post.cover_image,
        meta_title: post.meta_title, meta_description: post.meta_description,
        published_at: post.published_at, status: post.status || 'published',
        content: post.content || [],
      });
      setEditingSlug(slug);
      setView('editor');
    } catch {
      toast({ title: 'Error', description: 'Could not load post.', variant: 'destructive' });
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await deletePost(slug);
      toast({ title: 'Deleted', description: 'Post removed.' });
      loadPosts();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Delete failed.', variant: 'destructive' });
    }
  };

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setField('cover_image', url);
      toast({ title: 'Uploaded', description: 'Cover image uploaded.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Try a smaller image.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  // ---- Block editor helpers ----
  const addBlock = (type) => {
    const block = type === 'list' ? { type, items: [''] } : { type, text: '' };
    setForm((p) => ({ ...p, content: [...p.content, block] }));
  };
  const updateBlock = (index, patch) => {
    setForm((p) => ({
      ...p,
      content: p.content.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    }));
  };
  const removeBlock = (index) => {
    setForm((p) => ({ ...p, content: p.content.filter((_, i) => i !== index) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      content: form.content,
    };
    try {
      if (editingSlug) {
        await updatePost(editingSlug, payload);
        toast({ title: 'Saved', description: 'Post updated.' });
      } else {
        await createPost(payload);
        toast({ title: 'Published', description: 'New post created.' });
      }
      await loadPosts();
      setView('list');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-[#1A1A1A] text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-mono text-sm text-[#C8102E]">~/admin</span>
            <nav className="flex items-center gap-1">
              <button
                onClick={() => { setTab('posts'); setView('list'); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${tab === 'posts' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                data-testid="tab-posts"
              >
                <FileText size={15} /> Posts
              </button>
              <button
                onClick={() => { setTab('leads'); loadLeads(); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${tab === 'leads' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                data-testid="tab-leads"
              >
                <Mail size={15} /> Leads
              </button>
            </nav>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-gray-300 hover:text-white hover:bg-white/10 rounded-full"
            data-testid="admin-logout-button"
          >
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {tab === 'leads' ? (
          <div data-testid="leads-view">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Leads ({leads.length})</h1>
              <Button
                onClick={exportLeadsCSV}
                disabled={leads.length === 0}
                className="bg-[#1A1A1A] hover:bg-[#C8102E] text-white rounded-full px-6 disabled:opacity-40"
                data-testid="export-leads-button"
              >
                <Download size={16} className="mr-2" /> Export CSV
              </Button>
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setLeadFilter('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${leadFilter === 'all' ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}
                data-testid="lead-filter-all"
              >
                All ({leads.length})
              </button>
              {LEAD_STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => setLeadFilter(s)}
                  className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors ${leadFilter === s ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  data-testid={`lead-filter-${s}`}
                >
                  {s} ({leadCounts[s]})
                </button>
              ))}
            </div>

            {filteredLeads.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 border border-gray-100 text-center">
                <Mail size={32} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  {leads.length === 0
                    ? 'No leads yet. Submissions from the contact form will appear here.'
                    : `No ${leadFilter} leads.`}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="bg-white rounded-2xl p-6 border border-gray-100"
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-1 inline-block font-mono text-[10px] uppercase tracking-wide rounded-full px-2.5 py-1 ${STATUS_STYLES[lead.status || 'new']}`}
                          data-testid={`lead-status-badge-${lead.id}`}
                        >
                          {lead.status || 'new'}
                        </span>
                        <div>
                          <h3 className="font-bold text-[#1A1A1A]">{lead.name}</h3>
                          <a href={`mailto:${lead.email}`} className="text-sm text-[#C8102E] hover:underline">{lead.email}</a>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <span className="inline-block font-mono text-xs bg-[#FAFAFA] border border-gray-100 rounded-full px-3 py-1 text-gray-600">
                          {lead.service_type}
                        </span>
                        <Select
                          value={lead.status || 'new'}
                          onValueChange={(val) => handleLeadStatusChange(lead.id, val)}
                        >
                          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`lead-status-select-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {LEAD_STATUSES.map((s) => (
                              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="font-mono text-xs text-gray-400">
                          {new Date(lead.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed text-sm border-t border-gray-100 pt-3">
                      {lead.project_description}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : view === 'list' ? (
          <>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">Posts ({posts.length})</h1>
              <Button
                onClick={startNew}
                className="bg-[#C8102E] hover:bg-[#A00E26] text-white rounded-full px-6"
                data-testid="admin-new-post-button"
              >
                <Plus size={18} className="mr-2" /> New Post
              </Button>
            </div>

            <div className="space-y-3">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-white rounded-2xl p-5 border border-gray-100 flex items-center justify-between gap-4"
                  data-testid={`admin-post-row-${post.slug}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-mono text-xs text-gray-400 mb-1">
                      <span className="text-[#C8102E] uppercase">{post.category}</span>
                      <span>·</span>
                      <span>{post.published_at}</span>
                      {post.status === 'draft' && (
                        <span className="bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 uppercase tracking-wide" data-testid={`status-badge-${post.slug}`}>
                          Draft
                        </span>
                      )}
                      {post.status === 'published' && post.published_at > new Date().toISOString().slice(0, 10) && (
                        <span className="bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 uppercase tracking-wide">
                          Scheduled
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-[#1A1A1A] truncate">{post.title}</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer"
                       className="p-2 text-gray-400 hover:text-[#1A1A1A] transition-colors" title="View">
                      <ExternalLink size={18} />
                    </a>
                    <button onClick={() => startEdit(post.slug)}
                            className="p-2 text-gray-400 hover:text-[#C8102E] transition-colors"
                            data-testid={`admin-edit-${post.slug}`} title="Edit">
                      <Edit3 size={18} />
                    </button>
                    <button onClick={() => handleDelete(post.slug)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            data-testid={`admin-delete-${post.slug}`} title="Delete">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <form onSubmit={handleSave} className="max-w-3xl mx-auto" data-testid="admin-editor-form">
            <button type="button" onClick={() => setView('list')}
                    className="inline-flex items-center font-mono text-sm text-gray-500 hover:text-[#C8102E] mb-6">
              <ArrowLeft size={16} className="mr-2" /> back to posts
            </button>

            <div className="bg-white rounded-3xl p-8 border border-gray-100 space-y-6">
              <div>
                <Label className="font-semibold mb-2 block">Title *</Label>
                <Input value={form.title} onChange={(e) => setField('title', e.target.value)} required
                       data-testid="editor-title-input" />
              </div>
              <div>
                <Label className="font-semibold mb-2 block">Excerpt *</Label>
                <Textarea value={form.excerpt} onChange={(e) => setField('excerpt', e.target.value)} required rows={2}
                          data-testid="editor-excerpt-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold mb-2 block">Category *</Label>
                  <Input value={form.category} onChange={(e) => setField('category', e.target.value)} required
                         data-testid="editor-category-input" />
                </div>
                <div>
                  <Label className="font-semibold mb-2 block">Read time</Label>
                  <Input value={form.read_time} onChange={(e) => setField('read_time', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold mb-2 block">Author</Label>
                  <Input value={form.author} onChange={(e) => setField('author', e.target.value)} />
                </div>
                <div>
                  <Label className="font-semibold mb-2 block">Publish date (YYYY-MM-DD)</Label>
                  <Input value={form.published_at} onChange={(e) => setField('published_at', e.target.value)} placeholder="auto — future date = scheduled" data-testid="editor-date-input" />
                </div>
              </div>

              {/* Status */}
              <div>
                <Label className="font-semibold mb-2 block">Status</Label>
                <Select value={form.status} onValueChange={(val) => setField('status', val)}>
                  <SelectTrigger data-testid="editor-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-2">
                  Drafts are hidden from the site. Published posts with a future date are scheduled and go live automatically.
                </p>
              </div>

              <div>
                <Label className="font-semibold mb-2 block">Cover image</Label>
                {form.cover_image && (
                  <img src={form.cover_image} alt="cover preview" className="w-full h-40 object-cover rounded-xl mb-3 border border-gray-100" />
                )}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input value={form.cover_image} onChange={(e) => setField('cover_image', e.target.value)}
                         placeholder="Paste image URL or upload →"
                         data-testid="editor-cover-input" className="flex-1" />
                  <label className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-[#1A1A1A] text-white text-sm cursor-pointer hover:bg-[#C8102E] transition-colors whitespace-nowrap" data-testid="editor-upload-label">
                    {uploading ? 'Uploading…' : 'Upload Image'}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} data-testid="editor-upload-input" />
                  </label>
                </div>
              </div>
              <div>
                <Label className="font-semibold mb-2 block">Tags (comma-separated)</Label>
                <Input value={form.tags} onChange={(e) => setField('tags', e.target.value)} placeholder="AI, SEO" />
              </div>

              {/* SEO */}
              <div className="pt-4 border-t border-gray-100">
                <p className="font-mono text-xs text-[#C8102E] uppercase tracking-wider mb-4">SEO meta</p>
                <div className="space-y-4">
                  <div>
                    <Label className="font-semibold mb-2 block">Meta title</Label>
                    <Input value={form.meta_title} onChange={(e) => setField('meta_title', e.target.value)} placeholder="Defaults to title" />
                  </div>
                  <div>
                    <Label className="font-semibold mb-2 block">Meta description</Label>
                    <Textarea value={form.meta_description} onChange={(e) => setField('meta_description', e.target.value)} rows={2} placeholder="Defaults to excerpt" />
                  </div>
                </div>
              </div>

              {/* Content blocks */}
              <div className="pt-4 border-t border-gray-100">
                <p className="font-mono text-xs text-[#C8102E] uppercase tracking-wider mb-4">Content blocks</p>
                <div className="space-y-4">
                  {form.content.map((block, index) => (
                    <div key={index} className="bg-[#FAFAFA] rounded-xl p-4 border border-gray-100" data-testid={`editor-block-${index}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <GripVertical size={16} className="text-gray-300" />
                          <Select value={block.type} onValueChange={(val) => {
                            const newBlock = val === 'list' ? { type: val, items: block.items || [''] } : { type: val, text: block.text || '' };
                            updateBlock(index, newBlock);
                          }}>
                            <SelectTrigger className="w-36 h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {BLOCK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <button type="button" onClick={() => removeBlock(index)}
                                className="text-gray-400 hover:text-red-600 transition-colors p-1">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {block.type === 'list' ? (
                        <Textarea
                          value={(block.items || []).join('\n')}
                          onChange={(e) => updateBlock(index, { items: e.target.value.split('\n') })}
                          rows={4}
                          placeholder="One list item per line"
                          className="bg-white"
                        />
                      ) : (
                        <Textarea
                          value={block.text || ''}
                          onChange={(e) => updateBlock(index, { text: e.target.value })}
                          rows={block.type === 'paragraph' ? 4 : 2}
                          placeholder={`Enter ${block.type} text`}
                          className="bg-white"
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {BLOCK_TYPES.map((t) => (
                    <Button key={t} type="button" variant="outline" size="sm"
                            onClick={() => addBlock(t)}
                            className="rounded-full text-xs"
                            data-testid={`add-block-${t}`}>
                      <Plus size={14} className="mr-1" /> {t}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex gap-3">
                <Button type="submit" disabled={saving}
                        className="bg-[#C8102E] hover:bg-[#A00E26] text-white rounded-full px-8"
                        data-testid="editor-save-button">
                  {saving ? 'Saving…' : editingSlug ? 'Update Post' : 'Publish Post'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setView('list')} className="rounded-full">
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
